import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner/spinner';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import {
	parseNestedAdfDocument,
	type NestedAdfRendererWrapper,
} from '@confluence/macro-utils/entry-points/nestedAdfRenderer.ts';

import type { PageTreeNode as PageTreeNodeType } from './pageTreeTypes.ts';
import {
	extractChildNodes,
	extractSingleNodeAdfString,
	extractSingleNodeMediaToken,
	CHILDREN_PAGE_SIZE,
} from './pageTreeUtils.ts';
import type { ExcerptEntry } from './useExcerptBatchLoader.ts';
import { i18n } from './pagetreeMacroMessages.ts';
import { useNodeChildren } from './useNodeChildren.ts';
import PageTreeMacroChildrenQuery from './queries/__generated__/PageTreeMacroChildrenQuery.graphql.ts';
import type {
	PageTreeMacroChildrenQuery$data,
	PageTreeMacroChildrenQuery$variables,
} from './queries/__generated__/PageTreeMacroChildrenQuery.graphql.ts';
import PageTreeMacroExcerptQuery from './queries/__generated__/PageTreeMacroExcerptQuery.graphql.ts';
import type {
	PageTreeMacroExcerptQuery$data,
	PageTreeMacroExcerptQuery$variables,
} from './queries/__generated__/PageTreeMacroExcerptQuery.graphql.ts';

const styles = cssMap({
	nodeContainer: {
		paddingBlock: token('space.025'),
	},
	childrenContainer: {
		paddingInlineStart: token('space.200'),
	},
	nodeLabel: {
		display: 'flex',
		alignItems: 'flex-start',
	},
	toggleButton: {
		width: '12px',
		height: '24px',
		display: 'inline-flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexShrink: 0,
		cursor: 'pointer',
		backgroundColor: 'transparent',
		borderStyle: 'none',
		padding: token('space.0'),
	},
	toggleContainerLeaf: {
		width: '12px',
		height: '24px',
		display: 'inline-flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexShrink: 0,
	},
	dot: {
		width: '4px',
		height: '4px',
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.neutral.pressed'),
	},
	link: {
		textDecoration: 'none',
	},
	boldLink: {
		textDecoration: 'none',
		fontWeight: token('font.weight.bold'),
	},
	titleColumn: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: '0',
	},
	excerptWrapper: {
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
		paddingInlineStart: token('space.100'),
	},
});

interface PageTreeNodeComponentProps {
	node: PageTreeNodeType;
	showExcerpt: boolean;
	startDepth: number;
	currentDepth: number;
	currentPageId: string;
	spaceKey: string;
	spaceId: string | undefined;
	sort: string;
	reverse: boolean;
	/** Ancestor IDs for auto-expansion (current page's path from root) */
	ancestorIds: string[];
	RendererWrapper: NestedAdfRendererWrapper;
	/**
	 * Progressively populated map of excerpts produced by the renderer's
	 * paginated batch loader. The reference changes on every batch resolution,
	 * which fails `React.memo`'s shallow check and re-renders the whole tree.
	 * The reads below (`excerptMap.get(node.id)`) only produce a different
	 * value when *this* node's excerpt actually changed.
	 */
	excerptMap: ReadonlyMap<string, ExcerptEntry>;
	/**
	 * Enqueue IDs into the batch loader's pending queue. Stable reference --
	 * safe to use as an effect dependency.
	 */
	enqueueExcerptIds: (ids: readonly string[]) => void;
	/**
	 * Whether this node is part of the auto-expanded subtree (root level, or a
	 * descendant reached because a parent auto-expanded). When `true`, the
	 * node's excerpt is delivered via the batch loader and the per-node
	 * `PageTreeMacroExcerptQuery` is suppressed. When `false` (manual chevron
	 * expand), the per-node fallback fires.
	 */
	loadedViaAutoExpand: boolean;
}

const PageTreeNodeComponentImpl = ({
	node,
	showExcerpt,
	startDepth,
	currentDepth,
	currentPageId,
	spaceKey,
	spaceId,
	sort,
	reverse,
	ancestorIds,
	RendererWrapper,
	excerptMap,
	enqueueExcerptIds,
	loadedViaAutoExpand,
}: PageTreeNodeComponentProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const loadChildren = useNodeChildren(sort, reverse);
	const isAncestor = ancestorIds.includes(node.id);
	const isCurrentPage = node.id === currentPageId;
	const shouldAutoExpand = startDepth - currentDepth > 1 || isAncestor;

	// SSR-compatible: read preloaded children from cache for auto-expanding nodes
	const needsAutoExpandChildren = shouldAutoExpand && node.hasChildren && !node.children?.length;
	const autoExpandResult = useQueryInterop<
		PageTreeMacroChildrenQuery$data,
		PageTreeMacroChildrenQuery$variables
	>(PageTreeMacroChildrenQuery, {
		variables: { contentId: node.id, first: CHILDREN_PAGE_SIZE, after: null },
		skip: !needsAutoExpandChildren,
	});

	const autoExpandChildren = React.useMemo(
		(): PageTreeNodeType[] => extractChildNodes(autoExpandResult.data, sort, reverse) ?? [],
		[autoExpandResult.data, sort, reverse],
	);

	// Feed auto-expanded children IDs into the batch loader so the next batch
	// picks them up. This is the fix for the original coverage gap:
	// previously only the visible root IDs were batched, and every
	// auto-expanded child fell through to a per-node ExcerptQuery (N+1).
	useEffect(() => {
		if (!showExcerpt || autoExpandChildren.length === 0) return;
		enqueueExcerptIds(autoExpandChildren.map((c) => c.id));
	}, [showExcerpt, autoExpandChildren, enqueueExcerptIds]);

	// Read *this* node's entry from the batch result map. Undefined means
	// "not yet resolved by the batch loader" -- the per-node fallback below
	// is gated on `loadedViaAutoExpand` so it only fires for manual chevron
	// expansions, not for the race window before the batch lands.
	const entry = excerptMap.get(node.id);
	const storeAdfString = entry?.adfString;
	const storeMediaToken = entry?.mediaToken;

	// Per-node fallback only fires when this node was loaded by the user
	// clicking the chevron (i.e. NOT auto-expanded). Auto-expanded nodes are
	// always served from the batch loader; the gate is the prop, not the map
	// state -- otherwise children mount before the renderer's `enqueueExcerptIds`
	// effect runs and fire individual queries against the same IDs the batch
	// is about to fetch.
	const needsIndividualFetch = showExcerpt && !loadedViaAutoExpand;
	const individualResult = useQueryInterop<
		PageTreeMacroExcerptQuery$data,
		PageTreeMacroExcerptQuery$variables
	>(PageTreeMacroExcerptQuery, {
		variables: { id: node.id },
		skip: !needsIndividualFetch,
	});

	const excerptAdfString = storeAdfString ?? extractSingleNodeAdfString(individualResult.data);
	const excerptMediaToken = storeMediaToken ?? extractSingleNodeMediaToken(individualResult.data);
	const excerptAdfDocument = React.useMemo(
		() => (excerptAdfString ? parseNestedAdfDocument(excerptAdfString) : null),
		[excerptAdfString],
	);
	const hasExcerpt =
		showExcerpt && excerptAdfDocument != null && excerptAdfDocument.content.length > 0;

	const [state, setState] = useState({
		isExpanded: shouldAutoExpand,
		children: node.children ?? ([] as PageTreeNodeType[]),
		isLoading: false,
		childrenLoaded: !!node.children?.length,
	});

	// Merge: use auto-expand query result if state doesn't have children yet
	const resolvedChildren = state.childrenLoaded ? state.children : autoExpandChildren;
	const resolvedChildrenLoaded = state.childrenLoaded || autoExpandChildren.length > 0;
	const resolvedLoading = state.isLoading || (needsAutoExpandChildren && autoExpandResult.loading);
	// Children rendered out of `state.children` were loaded by a manual chevron
	// click; children rendered out of `autoExpandChildren` came in via the
	// auto-expand query and so should keep using the batch store for excerpts.
	const childrenLoadedViaAutoExpand = !state.childrenLoaded;

	const handleToggle = useCallback(async () => {
		if (state.isExpanded) {
			setState((prev) => ({ ...prev, isExpanded: false }));
			return;
		}

		if (!resolvedChildrenLoaded && node.hasChildren) {
			setState((prev) => ({ ...prev, isLoading: true }));
			const loadedChildren = await loadChildren(node.id);
			setState((prev) => ({
				...prev,
				children: loadedChildren,
				childrenLoaded: true,
				isExpanded: true,
				isLoading: false,
			}));
		} else {
			setState((prev) => ({ ...prev, isExpanded: true }));
		}
	}, [state.isExpanded, resolvedChildrenLoaded, node.hasChildren, node.id, loadChildren]);

	return (
		<Box xcss={styles.nodeContainer} testId={`pagetree-node-${node.id}`}>
			<Box xcss={styles.nodeLabel}>
				{node.hasChildren ? (
					<Pressable
						xcss={styles.toggleButton}
						onClick={handleToggle}
						aria-expanded={state.isExpanded}
						aria-label={state.isExpanded ? `Collapse ${node.title}` : `Expand ${node.title}`}
						testId={`pagetree-toggle-${node.id}`}
					>
						{state.isExpanded ? (
							<ChevronDownIcon
								label=""
								color={token('color.text.subtlest')}
								size="small"
								spacing="none"
							/>
						) : (
							<ChevronRightIcon
								label=""
								color={token('color.text.subtlest')}
								size="small"
								spacing="none"
							/>
						)}
					</Pressable>
				) : (
					<Box xcss={styles.toggleContainerLeaf}>
						<Box xcss={styles.dot} />
					</Box>
				)}
				<Box xcss={styles.titleColumn}>
					<Anchor
						href={node.url}
						testId={`pagetree-link-${node.id}`}
						xcss={isCurrentPage ? styles.boldLink : styles.link}
					>
						{node.title}
					</Anchor>
					{hasExcerpt && excerptAdfDocument && (
						<Box xcss={styles.excerptWrapper} testId={`pagetree-excerpt-${node.id}`}>
							<RendererWrapper
								adf={excerptAdfDocument.content}
								targetContentId={node.id}
								targetSpaceKey={spaceKey}
								targetSpaceId={spaceId}
								mediaToken={excerptMediaToken}
							/>
						</Box>
					)}
				</Box>
			</Box>

			{resolvedLoading && (
				<Box xcss={styles.childrenContainer}>
					<Inline alignBlock="center" space="space.100">
						<Spinner size="small" />
						<Text>{formatMessage(i18n.loading)}</Text>
					</Inline>
				</Box>
			)}

			{state.isExpanded && !resolvedLoading && resolvedChildren.length > 0 && (
				<Stack xcss={styles.childrenContainer} testId={`pagetree-children-${node.id}`}>
					{resolvedChildren.map((child) => (
						<PageTreeNodeComponent
							key={child.id}
							node={child}
							showExcerpt={showExcerpt}
							startDepth={startDepth}
							currentDepth={currentDepth + 1}
							currentPageId={currentPageId}
							spaceKey={spaceKey}
							spaceId={spaceId}
							sort={sort}
							reverse={reverse}
							ancestorIds={ancestorIds}
							RendererWrapper={RendererWrapper}
							excerptMap={excerptMap}
							enqueueExcerptIds={enqueueExcerptIds}
							loadedViaAutoExpand={childrenLoadedViaAutoExpand}
						/>
					))}
				</Stack>
			)}
		</Box>
	);
};

export const PageTreeNodeComponent: ({
	node,
	showExcerpt,
	startDepth,
	currentDepth,
	currentPageId,
	spaceKey,
	spaceId,
	sort,
	reverse,
	ancestorIds,
	RendererWrapper,
	excerptMap,
	enqueueExcerptIds,
	loadedViaAutoExpand,
}: PageTreeNodeComponentProps) => React.JSX.Element = PageTreeNodeComponentImpl;
